import React, { useEffect } from "react";
import { Flex } from "../../../Flex";
import { Title } from "../../../Title";
import { BasicNotificationBanner } from "../../../BasicNotificationBanner";
import { Span } from "../../../Span";
import { DisconnectionFormValues } from "../../../../types/DisconnectionFormValues";
import { DisconnectionPropertyDetailsForm } from "../DisconnectionPropertyDetailsForm";
import { Line } from "../../../Line";
import { DisconnectionDetailsForm } from "../DisconnectionDetailsForm";
import { DisconnectionUploadPhotosForm } from "../DisconnectionUploadPhotosForm";
import { DisconnectionContactDetailsForm } from "../DisconnectionContactDetailsForm";
import { getFirstErrorByPosition } from "../../../CheckErrorsUtil/CheckErrorsUtil";
import { handleScrollToName } from "../../../../utils/handleScroll/HandleScroll";
import { useFormikContext } from "formik";

interface Props {
  id: string;
  values: DisconnectionFormValues;
  validateField: (field: string) => void;
}

const sectionSeparator = (
  <Line
    my={5}
    width="100%"
    border="1px solid"
    borderColor="neutrals.ln30"
    bg="neutrals.ln30"
    opacity="1"
    height={0}
  />
);

export const DisconnectionOrderDetailsForm: React.FC<Props> = ({
  id,
  values,
  validateField,
}) => {
  // Scroll to first validation error (if any)
  const { errors, isSubmitting } = useFormikContext<DisconnectionFormValues>();

  useEffect(() => {
    if (isSubmitting) {
      const errorFieldName = getFirstErrorByPosition(errors);
      if (errorFieldName) {
        handleScrollToName(errorFieldName);
      }
    }
  }, [isSubmitting, errors]);

  return (
    <Flex id={id} flexDirection="column" mr={[0, 0, 0, 3]}>
      <Title as="h1" lineHeight={7} fontWeight="medium" mb={5} mt={5}>
        Order details
      </Title>

      <BasicNotificationBanner
        title=""
        message={() => (
          <Span>
            This application is for a <b>permanent disconnection</b> from the
            Vector network. This cannot be reversed. To submit this request, you
            will require either the{" "}
            <b>
              installation control point (ICP) number or the meter serial number
            </b>{" "}
            that is to be permanently disconnected. At least one of these must
            be provided. If you do not know the ICP number or meter serial
            number please call <a href="tel:+64508832867">0508 832 867</a> to
            request the permanent disconnection.
          </Span>
        )}
        icon="warning"
        variant="warning"
      />

      <DisconnectionPropertyDetailsForm
        id={`${id}-property-details-section`}
        propertyDetails={values.propertyDetails}
        validateField={validateField}
      />

      {sectionSeparator}

      <DisconnectionDetailsForm
        id={`${id}-disconnection-details-section`}
        disconnectionDetails={values.disconnectionDetails}
      ></DisconnectionDetailsForm>

      {sectionSeparator}

      <DisconnectionUploadPhotosForm id={`${id}-upload-photos-section`} />

      {sectionSeparator}

      <DisconnectionContactDetailsForm
        id={`${id}-disconnection-contact-details`}
        contactDetails={values.contactDetails}
      ></DisconnectionContactDetailsForm>
    </Flex>
  );
};
